/**
 * Fait : le formulaire d'une consigne — son titre, sa description, et son
 *   texte dans un CHAMP MULTILIGNE. Il sert aussi bien à en écrire une neuve
 *   qu'à en réécrire une existante : c'est le même formulaire, et le même
 *   ordre de champs, pour que le geste s'apprenne une fois.
 *
 *   IL COMPTE LES CARACTÈRES QUI RESTENT, et c'est pour le critère 145 autant
 *   que pour le confort : une consigne peut aller jusqu'à vingt mille
 *   caractères, et découvrir la borne au moment du refus, après avoir tout
 *   écrit, serait la découvrir trop tard. Le compte n'est annoncé aux lecteurs
 *   d'écran qu'aux approches de la borne — une région vive qui parle à chaque
 *   frappe rend la saisie inutilisable.
 *
 *   LA DESCRIPTION EST FACULTATIVE, et l'écran le dit. C'est elle qu'on
 *   retrouve par la recherche (critère 146), et personne ne peut le deviner :
 *   l'aide du champ l'explique en une phrase.
 *
 * Ne fait pas : il n'appelle pas le serveur — c'est l'écran qui envoie, et qui
 *   sait s'il s'agit d'une création ou d'une modification — et ne décide
 *   d'aucun droit : un formulaire qui ne s'affiche pas ne protège rien, c'est
 *   la route qui refuse (critère 149).
 */

import { useState } from 'react';

import {
  LONGUEUR_MAXIMALE_NOM_DONNE,
  TAILLE_MAXIMALE_DESCRIPTION_DE_PROMPT,
  TAILLE_MAXIMALE_TEXTE_DE_PROMPT,
} from '@aymoai/shared';

import { Champ } from '../gabarits/Champ.tsx';
import { ChampLong } from '../gabarits/ChampLong.tsx';
import type { DemandeDeConsigne } from './api-consignes.ts';

/** En deçà de ce reste, le compte est annoncé à voix haute. */
const SEUIL_D_ANNONCE = 500;

export function FormulaireDeConsigne({
  depart,
  libelleDEnvoi,
  enCours,
  surEnvoi,
  surAbandon,
}: {
  /** Ce qu'il y a déjà dans les champs — vide pour une consigne neuve. */
  readonly depart?: DemandeDeConsigne;

  readonly libelleDEnvoi: string;
  readonly enCours: boolean;
  readonly surEnvoi: (demande: DemandeDeConsigne) => void;
  readonly surAbandon?: (() => void) | undefined;
}) {
  const [titre, setTitre] = useState(depart?.titre ?? '');
  const [description, setDescription] = useState(depart?.description ?? '');
  const [texte, setTexte] = useState(depart?.texte ?? '');

  const reste = TAILLE_MAXIMALE_TEXTE_DE_PROMPT - texte.length;
  const trop = reste < 0;

  // Un titre et un texte : ce sont les deux seules choses qu'une consigne ne
  // peut pas ne pas avoir. La description, elle, peut rester vide.
  const complet = titre.trim() !== '' && texte.trim() !== '' && !trop;

  return (
    <form
      data-role="formulaire-de-consigne"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (complet && !enCours) surEnvoi({ titre, description, texte });
      }}
    >
      <Champ
        libelle="Titre de la consigne"
        aide="Ce qu’elle fait, en quelques mots — c’est ce qu’on lit dans la liste."
        name="titre-de-la-consigne"
        data-role="titre-de-la-consigne"
        maxLength={LONGUEUR_MAXIMALE_NOM_DONNE}
        value={titre}
        onChange={(evenement) => setTitre(evenement.target.value)}
      />

      <Champ
        libelle="Description (facultative)"
        aide="À quoi elle sert, ou quand s’en servir. La recherche fouille aussi cette phrase."
        name="description-de-la-consigne"
        data-role="description-de-la-consigne"
        maxLength={TAILLE_MAXIMALE_DESCRIPTION_DE_PROMPT}
        value={description}
        onChange={(evenement) => setDescription(evenement.target.value)}
      />

      <ChampLong
        libelle="La consigne elle-même"
        aide="Le texte qu’on reprendra tel quel. Jusqu’à vingt mille caractères."
        name="texte-de-la-consigne"
        data-role="texte-de-la-consigne"
        rows={10}
        value={texte}
        onChange={(evenement) => setTexte(evenement.target.value)}
        {...(trop ? { erreur: `Il y a ${String(-reste)} caractères de trop.` } : {})}
      />

      <p
        className="texte-attenue"
        data-role="reste-de-la-consigne"
        /*
         * `polite` seulement près de la borne : une région vive qui annonce un
         * nombre à chaque frappe couvre la voix du champ lui-même, et rend
         * l'écriture impossible pour qui écoute la page.
         */
        aria-live={reste <= SEUIL_D_ANNONCE ? 'polite' : 'off'}
      >
        {trop
          ? `${String(-reste)} caractères de trop.`
          : `${String(reste)} caractères encore disponibles.`}
      </p>

      <div className="barre-de-boutons">
        <button type="submit" data-role="principal" disabled={!complet || enCours}>
          {enCours ? 'Enregistrement…' : libelleDEnvoi}
        </button>

        {surAbandon !== undefined && (
          <button type="button" data-role="abandonner-la-consigne" onClick={surAbandon}>
            Annuler
          </button>
        )}
      </div>
    </form>
  );
}
